import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../../api/apiService';
import { storeTokens } from '../../auth/auth';
import { unmetPasswordRules } from './passwordPolicy';
import { PasswordRequirements, PasswordVisibilityToggle } from './PasswordInputs';

export const CHANGE_PASSWORD_URL = '/api/users/me/password';

type FieldName = 'current' | 'next' | 'confirm';
type FieldErrors = Partial<Record<FieldName, string>>;

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: '#fafafa' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3f3f46' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#71717a' },
  '& .MuiInputLabel-root': { color: '#a1a1aa' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
  '& .MuiFormHelperText-root:not(.Mui-error)': { color: '#a1a1aa' },
};

/**
 * Maps a flock-be error response to field errors / a general message.
 * Contract (flock-be PUT /api/users/me/password): a wrong current password is a 400
 * {"current_password": "..."} — deliberately NOT 401, which the axios interceptor would treat as
 * an expired token (refresh + replay the PUT). 400 {"new_password"} = policy; 429 = lockout;
 * 409 = changed concurrently (both with the message in "current_password").
 * Success = 200 {access_token, refresh_token}: the change signs out every existing session
 * (including this one), so the fresh pair must be stored to keep this tab signed in.
 */
const errorsFromApi = (error: any): { fields: FieldErrors; general?: string } => {
  const status: number | undefined = error?.status;
  const data = error?.data && typeof error.data === 'object' ? error.data : {};
  if (status === 400) {
    const fields: FieldErrors = {};
    if (typeof data.current_password === 'string') fields.current = data.current_password;
    if (typeof data.new_password === 'string') fields.next = data.new_password;
    if (fields.current || fields.next) return { fields };
    return { fields, general: 'The request was rejected. Please check the fields and try again.' };
  }
  if (status === 429 || status === 409) {
    const fallback =
      status === 429
        ? 'Too many attempts. Please try again later.'
        : 'Your password was changed in the meantime. Please try again.';
    return {
      fields: {},
      general: typeof data.current_password === 'string' ? data.current_password : fallback,
    };
  }
  return { fields: {}, general: 'Could not change your password. Please try again.' };
};

type PasswordFieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  autoComplete: string;
  autoFocus?: boolean;
  disabled?: boolean;
};

const PasswordField = ({ label, value, onChange, error, autoComplete, autoFocus, disabled }: PasswordFieldProps) => {
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      label={label}
      type={visible ? 'text' : 'password'}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      fullWidth
      margin="dense"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      helperText={error}
      sx={fieldSx}
      InputProps={{
        endAdornment: <PasswordVisibilityToggle visible={visible} onToggle={() => setVisible((v) => !v)} label={label} />,
      }}
    />
  );
};

export type ChangePasswordDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  // false only against an older backend (204, no tokens) that doesn't revoke other sessions.
  const [othersSignedOut, setOthersSignedOut] = useState(true);

  // Start clean every time the dialog opens; never keep passwords around after close.
  useEffect(() => {
    if (!open) return;
    setCurrent('');
    setNext('');
    setConfirm('');
    setFieldErrors({});
    setGeneralError(null);
    setSubmitting(false);
    setSucceeded(false);
    setOthersSignedOut(true);
  }, [open]);

  const validate = (): FieldErrors => {
    const errs: FieldErrors = {};
    if (!current) errs.current = 'Enter your current password';
    if (!next) {
      errs.next = 'Enter a new password';
    } else {
      const unmet = unmetPasswordRules(next);
      if (unmet.length > 0) errs.next = "Password doesn't meet the requirements below";
      else if (next === current) errs.next = 'New password must be different from the current one';
    }
    if (!confirm) errs.confirm = 'Confirm your new password';
    else if (next && confirm !== next) errs.confirm = 'Passwords do not match';
    return errs;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    const errs = validate();
    setFieldErrors(errs);
    setGeneralError(null);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const tokens = await api.put(CHANGE_PASSWORD_URL, { current_password: current, new_password: next });
      // Older backends answer 204 (no body) and don't revoke sessions; nothing to store then.
      const gotTokens = typeof tokens?.access_token === 'string' && typeof tokens?.refresh_token === 'string';
      if (gotTokens) storeTokens(tokens.access_token, tokens.refresh_token);
      setOthersSignedOut(gotTokens);
      setCurrent('');
      setNext('');
      setConfirm('');
      setSucceeded(true);
    } catch (error: any) {
      const { fields, general } = errorsFromApi(error);
      setFieldErrors(fields);
      setGeneralError(general ?? null);
    } finally {
      setSubmitting(false);
    }
  };

  const clearFieldError = (name: FieldName) =>
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="change-password-title"
      PaperProps={{ sx: { bgcolor: '#18181b', color: '#fafafa', border: '1px solid #27272a' } }}
    >
      <DialogTitle
        id="change-password-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          Change password
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} disabled={submitting} size="small" sx={{ color: '#a1a1aa' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {succeeded ? (
        <>
          <DialogContent>
            <Alert
              severity="success"
              variant="outlined"
              sx={{ color: '#bbf7d0', borderColor: '#166534', bgcolor: 'rgba(22,101,52,0.15)', '& .MuiAlert-icon': { color: '#4ade80' } }}
            >
              {othersSignedOut
                ? 'Your password has been changed. You stay signed in here; you have been signed out on all other devices and browsers, where you will need the new password.'
                : 'Your password has been changed. You stay signed in here; use the new password next time you sign in. Sessions on other devices are not signed out automatically.'}
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={onClose}
              variant="contained"
              sx={{ bgcolor: '#60a5fa', color: '#0a0a0a', textTransform: 'none', '&:hover': { bgcolor: '#3b82f6' } }}
            >
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent sx={{ pt: 0 }}>
            {generalError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {generalError}
              </Alert>
            )}
            <PasswordField
              label="Current password"
              autoComplete="current-password"
              autoFocus
              value={current}
              disabled={submitting}
              onChange={(v) => {
                setCurrent(v);
                clearFieldError('current');
              }}
              error={fieldErrors.current}
            />
            <Box sx={{ textAlign: 'right', mb: 1 }}>
              <RouterLink to="/forgot-password" onClick={onClose} style={{ color: '#60a5fa', fontSize: '0.8rem' }}>
                Forgot your current password?
              </RouterLink>
            </Box>
            <PasswordField
              label="New password"
              autoComplete="new-password"
              value={next}
              disabled={submitting}
              onChange={(v) => {
                setNext(v);
                clearFieldError('next');
              }}
              error={fieldErrors.next}
            />
            <PasswordRequirements password={next} />
            <PasswordField
              label="Confirm new password"
              autoComplete="new-password"
              value={confirm}
              disabled={submitting}
              onChange={(v) => {
                setConfirm(v);
                clearFieldError('confirm');
              }}
              error={fieldErrors.confirm}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose} disabled={submitting} sx={{ color: '#a1a1aa', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{ bgcolor: '#60a5fa', color: '#0a0a0a', textTransform: 'none', '&:hover': { bgcolor: '#3b82f6' } }}
            >
              {submitting ? <CircularProgress size={22} sx={{ color: '#0a0a0a' }} /> : 'Change password'}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
